// /pages/LotHistoryPage.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging, ColumnChooser, Grouping, GroupPanel
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import CheckBox from "devextreme-react/check-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const LOT_HISTORY_API_URL = `${API_URL}/api/oracle/lot-history`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;

export default function LotHistoryPage({ user }) {
  // State 관리
  const [data, setData] = useState([]);
  const [custList, setCustList] = useState([]);
  
  // 검색 조건 State
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [lotId, setLotId] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false); // delCheck ('Y'/'N')
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const dataGridRef = useRef(null);
  
  // useEffect 훅 (CUST 목록 조회 및 사용자 자동 선택)
  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => setCustList(res.data.data || []))
      .catch(err => console.error("CUST 목록 조회 실패:", err));
  }, [selectedFactory]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);
  
  const fetchData = () => {
    if (!selectedFactory || !selectedCust || !lotId) {
      notify("Factory, CUST, Lot ID는 필수 입력 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    const params = {
      factory: selectedFactory,
      custCd: selectedCust,
      lotId: lotId.trim().toUpperCase(),
      delCheck: includeDeleted ? 'Y' : 'N',
    };
    
    axios.get(LOT_HISTORY_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setData(res.data.data.map((row, idx) => ({ ...row, id: idx })));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message;
        setError(`조회 오류: ${errorMessage}`);
        notify(errorMessage, 'error', 3000);
        setData([]);
      })
      .finally(() => setLoading(false));
  };

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Lot_History");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Lot_History.xlsx"));
    e.cancel = true;
  };
  
  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>Lot History</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <SelectBox items={['FT01', 'PT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <TextBox value={lotId} onValueChanged={e => setLotId(e.value)} placeholder="Enter Lot ID" label="LOT ID" width={220} />
          <CheckBox value={includeDeleted} onValueChanged={e => setIncludeDeleted(e.value)} text="삭제 이력 포함" />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"></div>}
        <DataGrid
          ref={dataGridRef}
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          hoverStateEnabled={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Search..." />
          <Sorting mode="multiple" />
          <ColumnChooser enabled={true} />
    
          {/* ★★★ [수정] 컬럼 정의 ★★★ */}
          <Column dataField="HIST_SEQ" caption="Seq" width={80} fixed={true} />
          <Column dataField="TRAN_CODE" caption="Trans Code" width={120} fixed={true} />
          <Column dataField="LOT_DESC" caption="Description" width={200} />
          <Column dataField="LOT_ID" caption="Lot ID" width={150} />
          <Column dataField="DEVICE" caption="Device" width={150} />
          <Column dataField="DEVICE_FROM" caption="Device From" width={150} />
          <Column dataField="TRAN_TIME" caption="Trans Time" width={180} />
          <Column dataField="OPER_FROM" caption="Oper From" width={110} />
          <Column dataField="OPER_TO" caption="Oper To" width={110} />
          <Column dataField="QTY_FROM" caption="Qty From" dataType="number" alignment="right" width={100} />
          <Column dataField="QTY_TO" caption="Qty To" dataType="number" alignment="right" width={100} />
          <Column dataField="LOSS_QTY" caption="Loss Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="CV_QTY" caption="CV Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="SPLIT_LOT_ID" caption="Split Lot" width={150} />
          <Column dataField="MERGE_LOT_ID" caption="Merge Lot" width={150} />
          <Column dataField="CUSTOMER" caption="Customer" width={120} />
          <Column dataField="HOLD_CODE" caption="Hold Code" width={120} />
          <Column dataField="RES_ID" caption="Resource ID" width={120} />
          <Column dataField="HIST_DEL_FLAG" caption="Del Flag" width={90} alignment="center" />
          <Column dataField="HIST_DEL_TIME" caption="Del Time" width={180} />
          <Column dataField="CREATE_TIME" caption="Create Time" width={180} />
          <Column dataField="TRAN_USER_ID" caption="User ID" width={120} />
        </DataGrid>
      </div>
    </div>
  );
}